import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { clearPendingLoginReturn, getPendingLoginReturn } from '../utils/pendingLogin';

export const OAuthCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const { handleOAuth2Callback } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    const processCallback = async () => {
      const code = searchParams.get('code');
      const state = searchParams.get('state');
      const errorParam = searchParams.get('error');
      const errorDescription = searchParams.get('error_description');

      if (errorParam) {
        // Terminal outcome: the recorded deep link must not leak into a later,
        // unrelated login.
        clearPendingLoginReturn();
        setError(errorDescription || errorParam);
        setTimeout(() => navigate('/login'), 3000);
        return;
      }

      if (!code || !state) {
        clearPendingLoginReturn();
        setError(t('auth.oauth.missingParameters'));
        setTimeout(() => navigate('/login'), 3000);
        return;
      }

      try {
        const result = await handleOAuth2Callback(code, state);
        // `isAdmin` absent or false is an access-denied verdict: the provider
        // answered, but the user has no admin role. Mirrors the provider's own
        // check so the page and the auth context never disagree.
        if (result.error === 'access_denied' || result.isAdmin !== true) {
          clearPendingLoginReturn();
          navigate('/access-denied');
          return;
        }
        // Return the user to the route they originally requested (e.g. a
        // tenant-filtered invoices deep link), falling back to the dashboard.
        const pendingReturnPath = getPendingLoginReturn();
        clearPendingLoginReturn();
        navigate(pendingReturnPath || '/admin', { replace: true });
      } catch {
        clearPendingLoginReturn();
        setError(t('auth.oauth.callbackError'));
        setTimeout(() => navigate('/login'), 3000);
      }
    };

    processCallback();
  }, [searchParams, handleOAuth2Callback, navigate, t]);

  return (
    <div
      className="min-h-screen flex items-center justify-center p-5"
      style={{ background: 'var(--bg)' }}
    >
      <div
        className="max-w-md w-full p-12 text-center"
        style={{
          background: 'var(--surface)',
          borderRadius: 'var(--radius-lg)',
          boxShadow: 'var(--shadow)',
        }}
      >
        {error ? (
          <>
            <div className="inline-block mb-6">
              <svg className="w-14 h-14" fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{ color: 'var(--error)' }}>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2
              className="text-xl font-semibold mb-2"
              style={{ fontFamily: 'var(--font-display)', color: 'var(--text)' }}
            >
              {t('auth.oauth.authenticationFailed')}
            </h2>
            <p className="mb-3" style={{ color: 'var(--text-muted)' }}>{error}</p>
            <p className="text-sm" style={{ color: 'var(--text-muted)', opacity: 0.6 }}>
              {t('auth.oauth.redirectingToLogin')}
            </p>
          </>
        ) : (
          <>
            <div className="inline-block mb-6">
              <div
                className="w-14 h-14 rounded-full border-4 animate-spin"
                style={{ borderColor: 'var(--gold-pale)', borderTopColor: 'var(--gold)' }}
              />
            </div>
            <h2
              className="text-xl font-semibold mb-2"
              style={{ fontFamily: 'var(--font-display)', color: 'var(--text)' }}
            >
              {t('auth.oauth.authenticating')}
            </h2>
            <p style={{ color: 'var(--text-muted)' }}>{t('auth.oauth.pleaseWait')}</p>
          </>
        )}
      </div>
    </div>
  );
};
